/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';

// Floating pixel background component
export const FloatingPixelBackground: React.FC<{ intensity?: 'low' | 'medium' | 'high' }> = ({
  intensity = 'low'
}) => {
  const [pixels, setPixels] = useState<Array<{
    id: number;
    x: number;
    y: number;
    size: number;
    color: string;
    duration: number;
    delay: number;
    opacity: number;
  }>>([]);

  const pixelCount = intensity === 'low' ? 20 : intensity === 'medium' ? 35 : 50;

  useEffect(() => {
    const colors = ['#F97316', '#FB923C', '#EA580C', '#FDBA74', '#C2410C', '#FCD34D'];
    const generatedPixels = Array.from({ length: pixelCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      duration: Math.random() * 15 + 15,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.15 + 0.05,
    }));
    setPixels(generatedPixels);
  }, [pixelCount]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {pixels.map((pixel) => (
        <div
          key={pixel.id}
          className="absolute animate-float-pixel"
          style={{
            left: `${pixel.x}%`,
            top: `${pixel.y}%`,
            width: pixel.size,
            height: pixel.size,
            backgroundColor: pixel.color,
            opacity: pixel.opacity,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
            animationDuration: `${pixel.duration}s`,
            animationDelay: `${pixel.delay}s`,
          }}
        />
      ))}
      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(249, 115, 22, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(249, 115, 22, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
    </div>
  );
};

// Pixel block component
export const PixelBlock: React.FC<{
  color?: string;
  size?: number;
  className?: string;
}> = ({ color = '#F97316', size = 8, className = '' }) => {
  return (
    <div
      className={`relative ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        boxShadow: `inset -${size/4}px -${size/4}px 0 rgba(0,0,0,0.2), inset ${size/8}px ${size/8}px 0 rgba(255,255,255,0.1)`,
      }}
    />
  );
};

// Pixel Logo - Podstack 3D Stacked Pod in pixel art
export const PixelLogo: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const pixels = [
    // Top layer (stacked pod design)
    { x: 2, y: 0, c: '#FB923C' }, { x: 3, y: 0, c: '#FB923C' }, { x: 4, y: 0, c: '#F97316' }, { x: 5, y: 0, c: '#EA580C' },
    // Top layer sides
    { x: 1, y: 1, c: '#FB923C' }, { x: 6, y: 1, c: '#EA580C' },
    // Middle layer
    { x: 1, y: 2, c: '#F97316' }, { x: 2, y: 2, c: '#F97316' }, { x: 3, y: 2, c: '#FB923C' }, { x: 4, y: 2, c: '#F97316' }, { x: 5, y: 2, c: '#EA580C' }, { x: 6, y: 2, c: '#C2410C' },
    // Middle layer sides
    { x: 0, y: 3, c: '#F97316' }, { x: 7, y: 3, c: '#C2410C' },
    // Lower middle layer
    { x: 1, y: 4, c: '#F97316' }, { x: 2, y: 4, c: '#FB923C' }, { x: 3, y: 4, c: '#F97316' }, { x: 4, y: 4, c: '#F97316' }, { x: 5, y: 4, c: '#EA580C' }, { x: 6, y: 4, c: '#C2410C' },
    // Connection nodes
    { x: 0, y: 5, c: '#FB923C' }, { x: 7, y: 5, c: '#EA580C' },
    // Bottom layer
    { x: 2, y: 6, c: '#EA580C' }, { x: 3, y: 6, c: '#F97316' }, { x: 4, y: 6, c: '#F97316' }, { x: 5, y: 6, c: '#C2410C' },
  ];

  const size = 4 * scale;

  return (
    <div
      className={`relative ${animate ? 'animate-pulse-subtle' : ''}`}
      style={{ width: size * 8, height: size * 7 }}
    >
      {pixels.map((pixel, i) => (
        <div
          key={i}
          className="absolute transition-all duration-300"
          style={{
            left: pixel.x * size,
            top: pixel.y * size,
            width: size,
            height: size,
            backgroundColor: pixel.c,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
          }}
        />
      ))}
    </div>
  );
};

// Pixel GPU chip
export const PixelGPU: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const pixels = [
    // Main body (orange)
    { x: 2, y: 1, c: '#F97316' }, { x: 3, y: 1, c: '#F97316' }, { x: 4, y: 1, c: '#F97316' }, { x: 5, y: 1, c: '#F97316' },
    { x: 1, y: 2, c: '#F97316' }, { x: 2, y: 2, c: '#FB923C' }, { x: 3, y: 2, c: '#FB923C' }, { x: 4, y: 2, c: '#FB923C' }, { x: 5, y: 2, c: '#FB923C' }, { x: 6, y: 2, c: '#F97316' },
    { x: 1, y: 3, c: '#F97316' }, { x: 2, y: 3, c: '#FB923C' }, { x: 3, y: 3, c: '#FFF' }, { x: 4, y: 3, c: '#FFF' }, { x: 5, y: 3, c: '#FB923C' }, { x: 6, y: 3, c: '#F97316' },
    { x: 1, y: 4, c: '#F97316' }, { x: 2, y: 4, c: '#FB923C' }, { x: 3, y: 4, c: '#FFF' }, { x: 4, y: 4, c: '#FFF' }, { x: 5, y: 4, c: '#FB923C' }, { x: 6, y: 4, c: '#F97316' },
    { x: 1, y: 5, c: '#F97316' }, { x: 2, y: 5, c: '#FB923C' }, { x: 3, y: 5, c: '#FB923C' }, { x: 4, y: 5, c: '#FB923C' }, { x: 5, y: 5, c: '#FB923C' }, { x: 6, y: 5, c: '#F97316' },
    { x: 2, y: 6, c: '#F97316' }, { x: 3, y: 6, c: '#F97316' }, { x: 4, y: 6, c: '#F97316' }, { x: 5, y: 6, c: '#F97316' },
    // Pins (dark)
    { x: 0, y: 3, c: '#1F2937' }, { x: 0, y: 4, c: '#1F2937' },
    { x: 7, y: 3, c: '#1F2937' }, { x: 7, y: 4, c: '#1F2937' },
    { x: 2, y: 0, c: '#1F2937' }, { x: 3, y: 0, c: '#1F2937' }, { x: 4, y: 0, c: '#1F2937' }, { x: 5, y: 0, c: '#1F2937' },
    { x: 2, y: 7, c: '#1F2937' }, { x: 3, y: 7, c: '#1F2937' }, { x: 4, y: 7, c: '#1F2937' }, { x: 5, y: 7, c: '#1F2937' },
  ];

  const size = 4 * scale;

  return (
    <div
      className={`relative ${animate ? 'animate-wiggle' : ''}`}
      style={{ width: size * 8, height: size * 8 }}
    >
      {pixels.map((pixel, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: pixel.x * size,
            top: pixel.y * size,
            width: size,
            height: size,
            backgroundColor: pixel.c,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
          }}
        />
      ))}
    </div>
  );
};

// Pixel Container/Pod
export const PixelContainer: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const pixels = [
    // Container top
    { x: 1, y: 0, c: '#F97316' }, { x: 2, y: 0, c: '#FB923C' }, { x: 3, y: 0, c: '#FB923C' }, { x: 4, y: 0, c: '#FB923C' }, { x: 5, y: 0, c: '#F97316' },
    { x: 0, y: 1, c: '#EA580C' }, { x: 1, y: 1, c: '#F97316' }, { x: 2, y: 1, c: '#FB923C' }, { x: 3, y: 1, c: '#FB923C' }, { x: 4, y: 1, c: '#FB923C' }, { x: 5, y: 1, c: '#F97316' }, { x: 6, y: 1, c: '#EA580C' },
    // Container body
    { x: 0, y: 2, c: '#EA580C' }, { x: 1, y: 2, c: '#F97316' }, { x: 2, y: 2, c: '#FFF' }, { x: 3, y: 2, c: '#FFF' }, { x: 4, y: 2, c: '#FFF' }, { x: 5, y: 2, c: '#F97316' }, { x: 6, y: 2, c: '#EA580C' },
    { x: 0, y: 3, c: '#C2410C' }, { x: 1, y: 3, c: '#EA580C' }, { x: 2, y: 3, c: '#F97316' }, { x: 3, y: 3, c: '#F97316' }, { x: 4, y: 3, c: '#F97316' }, { x: 5, y: 3, c: '#EA580C' }, { x: 6, y: 3, c: '#C2410C' },
    { x: 0, y: 4, c: '#C2410C' }, { x: 1, y: 4, c: '#EA580C' }, { x: 2, y: 4, c: '#F97316' }, { x: 3, y: 4, c: '#F97316' }, { x: 4, y: 4, c: '#F97316' }, { x: 5, y: 4, c: '#EA580C' }, { x: 6, y: 4, c: '#C2410C' },
    // Container bottom
    { x: 1, y: 5, c: '#C2410C' }, { x: 2, y: 5, c: '#EA580C' }, { x: 3, y: 5, c: '#EA580C' }, { x: 4, y: 5, c: '#EA580C' }, { x: 5, y: 5, c: '#C2410C' },
  ];

  const size = 4 * scale;

  return (
    <div
      className={`relative ${animate ? 'animate-bounce-subtle' : ''}`}
      style={{ width: size * 7, height: size * 6 }}
    >
      {pixels.map((pixel, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: pixel.x * size,
            top: pixel.y * size,
            width: size,
            height: size,
            backgroundColor: pixel.c,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
          }}
        />
      ))}
    </div>
  );
};

// Pixel Server
export const PixelServer: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const [lights, setLights] = useState([true, false, true, true]);

  useEffect(() => {
    if (!animate) return;
    const interval = setInterval(() => {
      setLights(prev => prev.map(() => Math.random() > 0.3));
    }, 500);
    return () => clearInterval(interval);
  }, [animate]);

  const size = 4 * scale;

  return (
    <div className="relative" style={{ width: size * 6, height: size * 8 }}>
      {/* Server body */}
      {[0, 1, 2, 3, 4, 5].map(x =>
        [0, 1, 2, 3, 4, 5, 6, 7].map(y => (
          <div
            key={`${x}-${y}`}
            className="absolute"
            style={{
              left: x * size,
              top: y * size,
              width: size,
              height: size,
              backgroundColor: y === 0 || y === 7 ? '#1F2937' :
                             y % 3 === 0 ? '#374151' : '#111827',
              boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.4), inset 1px 1px 0 rgba(255,255,255,0.05)`,
            }}
          />
        ))
      )}
      {/* Status lights */}
      {lights.map((on, i) => (
        <div
          key={i}
          className="absolute transition-all duration-200"
          style={{
            left: size,
            top: (i * 2 + 1) * size,
            width: size,
            height: size,
            backgroundColor: on ? '#F97316' : '#4B5563',
            boxShadow: on ? '0 0 6px #F97316' : 'none',
          }}
        />
      ))}
    </div>
  );
};

// Pixel Bucket/Storage
export const PixelBucket: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const pixels = [
    // Bucket rim
    { x: 1, y: 0, c: '#F97316' }, { x: 2, y: 0, c: '#FB923C' }, { x: 3, y: 0, c: '#FB923C' }, { x: 4, y: 0, c: '#FB923C' }, { x: 5, y: 0, c: '#F97316' },
    // Bucket body
    { x: 0, y: 1, c: '#EA580C' }, { x: 1, y: 1, c: '#F97316' }, { x: 2, y: 1, c: '#FFF' }, { x: 3, y: 1, c: '#FFF' }, { x: 4, y: 1, c: '#FFF' }, { x: 5, y: 1, c: '#F97316' }, { x: 6, y: 1, c: '#EA580C' },
    { x: 0, y: 2, c: '#EA580C' }, { x: 1, y: 2, c: '#F97316' }, { x: 2, y: 2, c: '#FB923C' }, { x: 3, y: 2, c: '#FB923C' }, { x: 4, y: 2, c: '#FB923C' }, { x: 5, y: 2, c: '#F97316' }, { x: 6, y: 2, c: '#EA580C' },
    { x: 0, y: 3, c: '#C2410C' }, { x: 1, y: 3, c: '#EA580C' }, { x: 2, y: 3, c: '#F97316' }, { x: 3, y: 3, c: '#F97316' }, { x: 4, y: 3, c: '#F97316' }, { x: 5, y: 3, c: '#EA580C' }, { x: 6, y: 3, c: '#C2410C' },
    // Bucket bottom
    { x: 1, y: 4, c: '#C2410C' }, { x: 2, y: 4, c: '#C2410C' }, { x: 3, y: 4, c: '#C2410C' }, { x: 4, y: 4, c: '#C2410C' }, { x: 5, y: 4, c: '#C2410C' },
  ];

  const size = 4 * scale;

  return (
    <div
      className={`relative ${animate ? 'animate-wiggle' : ''}`}
      style={{ width: size * 7, height: size * 5 }}
    >
      {pixels.map((pixel, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: pixel.x * size,
            top: pixel.y * size,
            width: size,
            height: size,
            backgroundColor: pixel.c,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
          }}
        />
      ))}
    </div>
  );
};

// Pixel Wallet
export const PixelWallet: React.FC<{ scale?: number; animate?: boolean }> = ({
  scale = 1,
  animate = false
}) => {
  const pixels = [
    // Wallet body
    { x: 0, y: 0, c: '#F97316' }, { x: 1, y: 0, c: '#FB923C' }, { x: 2, y: 0, c: '#FB923C' }, { x: 3, y: 0, c: '#FB923C' }, { x: 4, y: 0, c: '#FB923C' }, { x: 5, y: 0, c: '#F97316' },
    { x: 0, y: 1, c: '#EA580C' }, { x: 1, y: 1, c: '#F97316' }, { x: 2, y: 1, c: '#FDBA74' }, { x: 3, y: 1, c: '#FDBA74' }, { x: 4, y: 1, c: '#F97316' }, { x: 5, y: 1, c: '#EA580C' },
    { x: 0, y: 2, c: '#EA580C' }, { x: 1, y: 2, c: '#F97316' }, { x: 2, y: 2, c: '#FFF' }, { x: 3, y: 2, c: '#FFF' }, { x: 4, y: 2, c: '#F97316' }, { x: 5, y: 2, c: '#EA580C' }, { x: 6, y: 2, c: '#22C55E' },
    { x: 0, y: 3, c: '#C2410C' }, { x: 1, y: 3, c: '#EA580C' }, { x: 2, y: 3, c: '#F97316' }, { x: 3, y: 3, c: '#F97316' }, { x: 4, y: 3, c: '#EA580C' }, { x: 5, y: 3, c: '#C2410C' },
  ];

  const size = 4 * scale;

  return (
    <div
      className={`relative ${animate ? 'animate-bounce-subtle' : ''}`}
      style={{ width: size * 7, height: size * 4 }}
    >
      {pixels.map((pixel, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: pixel.x * size,
            top: pixel.y * size,
            width: size,
            height: size,
            backgroundColor: pixel.c,
            boxShadow: `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 rgba(255,255,255,0.2)`,
          }}
        />
      ))}
    </div>
  );
};

// Pixel decorative corner
export const PixelCorner: React.FC<{
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  size?: number;
}> = ({ position, size = 4 }) => {
  const rotations = {
    'top-left': 0,
    'top-right': 90,
    'bottom-right': 180,
    'bottom-left': 270,
  };

  return (
    <div
      className="absolute opacity-30"
      style={{
        [position.includes('top') ? 'top' : 'bottom']: 0,
        [position.includes('left') ? 'left' : 'right']: 0,
        transform: `rotate(${rotations[position]}deg)`,
      }}
    >
      <div className="flex flex-col">
        {[0, 1, 2].map(row => (
          <div key={row} className="flex">
            {[0, 1, 2].map(col => (
              <div
                key={col}
                style={{
                  width: size,
                  height: size,
                  backgroundColor: row + col <= 2 ? '#F97316' : 'transparent',
                  opacity: 1 - (row + col) * 0.3,
                  boxShadow: row + col <= 2 ? `inset -1px -1px 0 rgba(0,0,0,0.3)` : 'none',
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// All components are exported as named exports above
